import { Component, Inject, OnDestroy } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash-es';
import { DateTime } from 'luxon';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ConfirmationDialogComponent } from 'src/app/components/shared/confirmation-dialog/confirmation-dialog.component';
import { SensorData, SensorDataGraph } from 'src/app/model/sensorData';
import { ExecutionService } from 'src/app/services/execution.service';
import { SnackBarService } from 'src/app/services/snackBar.service';
import { SpinnerService } from 'src/app/services/spinner.service';
import { ArrayUtils } from 'src/app/utils/arrayUtils';
import { DateUtils } from 'src/app/utils/dateUtils';

export interface DialogDataDiscardTimeEdit {
  idExecution: number;
  idEssay: number;
  data: SensorData[];
}

@Component({
    selector: 'app-execution-edit-discard-time',
    templateUrl: './execution-edit-discard-time.component.html',
    standalone: false,
})
export class ExecutionEditDiscardTimeComponent implements OnDestroy {

  startDateStr: string;
  endDateStr: string;

  startDateRange: Date;
  endDateRange: Date;

  /** Motivo del descarte: obligatorio, va al audit trail de la ejecucion. */
  reason: string;

  datasource: SensorDataGraph[];
  sensors: any[];

  /** Generacion de la seleccion: cada cambio de rango (arrastre o campos) la incrementa. */
  private selectionSeq = 0;

  /** Hay una confirmacion o un borrado en curso: no se abre otro. */
  private discarding = false;

  private destroy$ = new Subject<void>();

  constructor(
    private dialogRef: MatDialogRef<ExecutionEditDiscardTimeComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogDataDiscardTimeEdit,
    private executionService: ExecutionService,
    private translate: TranslateService,
    public snackBarService: SnackBarService,
    private spinnerService: SpinnerService,
    private dialog: MatDialog) {

    this.startDateRange = _.cloneDeep(this.data.data.sort((a, b) => a.date.getTime() - b.date.getTime()).find(i => i.date != null).date);
    this.endDateRange = _.cloneDeep(DateUtils.anyToDate(this.startDateRange));
    this.endDateRange.setHours(this.endDateRange.getHours() + 1);

    if (this.startDateStr == null || this.startDateStr === '') {
      this.convertDateToString(this.startDateRange, this.endDateRange);
    }

    this.recalculateDatasource(this.data.data);

  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onOkClick(): void {
    const errs = [];

    const startDate = DateTime.fromFormat(this.startDateStr, 'dd/MM/yyyy HH:mm:ss');
    this.startDateRange = startDate.isValid ? startDate.toJSDate() : null;

    const endDate = DateTime.fromFormat(this.endDateStr, 'dd/MM/yyyy HH:mm:ss');
    this.endDateRange = endDate.isValid ? endDate.toJSDate() : null;

    if (this.startDateRange == null) {
      errs.push(this.translate.instant('executionEdit.discardTime.form.error.startDate'));
    }
    if (this.endDateRange == null) {
      errs.push(this.translate.instant('executionEdit.discardTime.form.error.endDate'));
    }
    // Antes de un borrado irreversible: un rango invertido no se manda al back.
    if (this.startDateRange != null && this.endDateRange != null && this.startDateRange.getTime() > this.endDateRange.getTime()) {
      errs.push(this.translate.instant('executionEdit.discardTime.form.error.range'));
    }
    // El back lo exige: sin motivo no se descarta nada, porque el asiento del audit trail
    // tiene que decir por que se excluyeron esas lecturas.
    if (this.reason == null || this.reason.trim() === '') {
      errs.push(this.translate.instant('executionEdit.discardTime.form.error.reason'));
    }

    if (errs.length === 0) {
      if (this.discarding) {
        return;
      }

      // QLOD-52: el borrado es fisico y no se puede deshacer ni recuperar la grafica anterior.
      // Se avisa antes, con el rango elegido, en vez de que se descubra despues. Se captura ese
      // rango y el motivo: lo que se borra es exactamente lo anunciado aunque cambien los campos
      // mientras tanto.
      const start = new Date(this.startDateRange.getTime());
      const end = new Date(this.endDateRange.getTime());
      const motivo = this.reason.trim();
      const message = this.translate.instant('executionEdit.discardTime.form.confirm',
        { start: this.startDateStr, end: this.endDateStr }) as string;

      this.discarding = true;
      this.dialog.open(ConfirmationDialogComponent, { data: { message } })
        .afterClosed().pipe(takeUntil(this.destroy$)).subscribe(confirmed => {
          if (confirmed === true) {
            this.discard(start, end, motivo);
          } else {
            this.discarding = false;
          }
        });
    } else {
      const error = errs.join('\n');
      this.snackBarService.sendError(error);
    }
  }

  private discard(start: Date, end: Date, motivo: string): void {
    this.spinnerService.show();

    this.executionService.discardTime(this.data.idExecution, this.data.idEssay, start, end, motivo)
      .pipe(takeUntil(this.destroy$)).subscribe(res => {
        this.spinnerService.hide();
        this.discarding = false;

        this.snackBarService.sendSuccess(this.translate.instant('executionEdit.discardTime.form.save.ok') as string);

        this.dialogRef.close({
          valid: true,
          data: res
        });
      }, err => {
        const message = typeof err?.error === 'string' && err.error.trim() !== ''
          ? err.error : this.translate.instant('executionEdit.discardTime.form.save.error.generic') as string;
        this.snackBarService.sendError(message);
        this.spinnerService.hide();
        this.discarding = false;
      });
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  recalculateDatasource(data: SensorData[]): void {
    this.spinnerService.show();

    if (data == null || ArrayUtils.isEmpty(data)) {
      this.datasource = [];
      this.sensors = [];

      return;
    }

    const res: SensorDataGraph[] = [];
    const sensors = [];

    const groupBy = ArrayUtils.groupBy(data, (item: SensorData) => item.date);
    groupBy.forEach((values: SensorData[], key: Date) => {
      values.forEach(value => {
        const sen = new SensorDataGraph();
        sen.date = key;

        if (!value.equipmentName) {
          return;
        }

        const beautySerialNum = value.equipmentName.replace('-', '').replace(' ', '').toLowerCase();

        sen[beautySerialNum] = value.value;

        const currentSensors = sensors.map(item => item.name);

        if (!currentSensors.includes(value.equipmentName)) {

          sensors.push({
            name: value.equipmentName,
            serialNum: value.serialNum,
            value: beautySerialNum
          });
        }

        res.push(sen);
      });
    });

    this.datasource = res;
    this.sensors = sensors;

    this.data.data.forEach(item => item.date = DateUtils.anyToDate(item.date));

    this.spinnerService.hide();
  }

  /**
   * Lo tecleado a mano mueve la franja de la grafica (antes solo iba de la franja a los campos y
   * la grafica se quedaba en el tramo anterior: validacion de Elena, 21-oct). Si los campos no son
   * validos o el rango esta invertido no se mueve nada: onOkClick ya lo rechaza con su aviso.
   */
  onDateFieldChange(): void {
    const start = DateTime.fromFormat(this.startDateStr ?? '', 'dd/MM/yyyy HH:mm:ss');
    const end = DateTime.fromFormat(this.endDateStr ?? '', 'dd/MM/yyyy HH:mm:ss');

    if (!start.isValid || !end.isValid || start.toMillis() > end.toMillis()) {
      return;
    }

    this.selectionSeq++;
    this.startDateRange = start.toJSDate();
    this.endDateRange = end.toJSDate();
  }

  onValueChanged = (e) => {
    // Eco de onDateFieldChange: la franja ya esta donde dicen los campos; no se reajusta al dato
    // mas cercano ni se reescribe lo tecleado.
    if (this.startDateRange != null && this.endDateRange != null
      && e.value[0].getTime() === this.startDateRange.getTime() && e.value[1].getTime() === this.endDateRange.getTime()) {
      return;
    }

    // Los campos toman exactamente donde se suelta la franja (al segundo, como sus marcadores). Antes
    // se ajustaban a la lectura mas cercana y la franja se quedaba donde se solto: arriba 14:22:53 y
    // abajo 14:23:00, o dos horas si habia un hueco en los datos (validacion de Elena, 21-oct). Para
    // el dato no hace falta ajustar: el descarte borra todas las lecturas dentro del tramo.
    this.selectionSeq++;
    const initDate = new Date(Math.floor(e.value[0].getTime() / 1000) * 1000);
    const endDate = new Date(Math.floor(e.value[1].getTime() / 1000) * 1000);

    this.startDateRange = initDate;
    this.endDateRange = endDate;

    this.convertDateToString(initDate, endDate);
  }


  customizeLabel = (data) => {
    return data.value.toLocaleString();
  }

  customizeSliderMarker = (data) => {
    return data.value.toLocaleString();
  }

  private convertDateToString(startDate: Date, endDate: Date): void {
    try {
      if (startDate == null) {
        this.startDateStr = null;
      } else {
        const date = startDate;
        const start = DateTime.fromJSDate(date).toFormat('dd/MM/yyyy HH:mm:ss');

        this.startDateStr = start;
      }
    } catch (e) {
      this.startDateStr = null;
    }

    try {
      if (endDate == null) {
        this.startDateStr = null;
      } else {
        const date = endDate;
        const end = DateTime.fromJSDate(date).toFormat('dd/MM/yyyy HH:mm:ss');

        this.endDateStr = end;
      }
    } catch (e) {
      this.startDateStr = null;
    }
  }

}
